import { FormEvent, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { KeyRound } from 'lucide-react'
import { authService } from '@/services/authService'

const ResetPasswordPage = () => {
  const [params] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [email, setEmail] = useState(params.get('email') || '')
  const [otp, setOtp] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const loginType = params.get('type') === 'member' ? 'member' : 'staff'
  const loginPath = loginType === 'member' ? '/login' : '/user-login'

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setIsLoading(true)
    try {
      await authService.resetPassword(email, otp, password)
      navigate(loginPath, { replace: true, state: { passwordReset: true } })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to reset password.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 px-4 py-10 flex items-center justify-center">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 sm:p-8 shadow-xl border border-gray-100">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
          <KeyRound className="h-7 w-7" />
        </div>
        <h1 className="text-center text-2xl font-bold text-gray-900">Enter OTP</h1>
        <p className="mt-2 text-center text-sm text-gray-600">
          {(location.state as { otpSent?: boolean } | null)?.otpSent ? 'We sent an OTP to your registered email.' : 'Enter the OTP sent to your registered email.'}
        </p>
        <form onSubmit={handleSubmit} className="mt-7 space-y-4">
          {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-700">Email</label>
            <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl border border-gray-300 px-3 py-3 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200" />
          </div>
          <div>
            <label htmlFor="otp" className="mb-1.5 block text-sm font-medium text-gray-700">6-digit OTP</label>
            <input id="otp" inputMode="numeric" autoComplete="one-time-code" required maxLength={6} pattern="[0-9]{6}" value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full rounded-xl border border-gray-300 px-3 py-3 text-center text-2xl font-bold tracking-[0.45em] focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200" />
          </div>
          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-gray-700">New password</label>
            <input id="password" type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Uppercase, lowercase, number and symbol" className="w-full rounded-xl border border-gray-300 px-3 py-3 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200" />
          </div>
          <div>
            <label htmlFor="confirmPassword" className="mb-1.5 block text-sm font-medium text-gray-700">Confirm password</label>
            <input id="confirmPassword" type="password" required minLength={8} autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full rounded-xl border border-gray-300 px-3 py-3 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200" />
          </div>
          <button type="submit" disabled={isLoading || otp.length !== 6} className="w-full rounded-xl bg-emerald-600 px-4 py-3 font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
            {isLoading ? 'Updating password…' : 'Update password'}
          </button>
        </form>
        <div className="mt-5 flex justify-between text-sm">
          <Link to={`/forgot-password?type=${loginType}`} className="font-medium text-emerald-700">Resend OTP</Link>
          <Link to={loginPath} className="text-gray-600">Back to Login</Link>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage
